// Frozen copy of main's code for the document_generation_v4_enabled / agency_brand_design_enabled flag-off path; delete with the flag.
import React from 'react';
import clsx from 'clsx';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor as EditorType } from '@tiptap/react';
import type { AnyExtension } from '@tiptap/core';

import { EDITOR_SCOPE_ID } from './editor-css';
import { extractBodyContent } from '~/components/document-editor/template';
import { useEditorContentSync } from './use-editor-content-sync';
import {
  type EditorSaveStatus,
  useEditorDocumentPersistence
} from './use-editor-document-persistence';
import {
  useEditorImageSetup,
  type EditorResizeControlsState
} from '~/components/document-editor/use-editor-image-setup';
import { useEditorPreview } from './use-editor-preview';
import { useEditorStylesheets } from './use-editor-stylesheets';
import { ImageResizeControls } from '~/components/document-editor/editor-image-resize-controls';
import { buildEditorExtensions } from './build-editor-extensions';
import { MenuBar } from './editor-menu-bar';
import { EditorPreviewPane } from './editor-preview-pane';

export type HtmlDocumentEditorProps = {
  content: string;
  editable: boolean;
  /** Debounced, for saving as the user types. */
  onUpdate?: (content: string) => void | Promise<void>;
  /** Called on every edit, for a form that reads the content when it submits. */
  onChange?: (content: string) => void;
  onSaveStatusChange?: (status: EditorSaveStatus) => void;
  workspaceId?: string;
  templateId?: string;
  artifactId?: string;
  artifactVersion?: number;
  showPreview?: boolean;
  onPreviewToggle?: () => void;
  showImageButton?: boolean;
  /**
   * `bare` drops the frame and the writing area's own scrollbar, and pins the
   * toolbar to the top of whatever is scrolling outside it. It is for the mail
   * composer, where the header's own hairline already bounds the body: a
   * scrollable editor inside a scrollable page gives the reader two bars that
   * mean different amounts of the same letter, and neither one measures it.
   */
  chrome?: 'framed' | 'bare';
  /** Added after the editor's own extensions. Read once, when the editor is created. */
  extensions?: AnyExtension[];
  /** Caller controls shown under the formatting toolbar. */
  toolbarControls?: (editor: EditorType) => React.ReactNode;
  /** False leaves out the toolbar, for a read-only view rather than a briefly locked one. */
  showToolbar?: boolean;
  /** Called with the TipTap editor once it exists, for a caller that edits the content itself. */
  onEditorReady?: (editor: EditorType) => void;
  /** The writing area's accessible name. Read once, when the editor is created. */
  ariaLabel?: string;
};

const ignoreUpdate = () => {};

/**
 * TipTap editor for HTML document artifacts and library template bodies.
 * Composes toolbar, preview, extensions, save/sync, and stylesheet injection.
 */
export const HtmlDocumentEditor = ({
  content,
  editable,
  onUpdate = ignoreUpdate,
  onChange,
  onSaveStatusChange,
  workspaceId,
  templateId,
  artifactId,
  artifactVersion,
  showPreview,
  onPreviewToggle,
  showImageButton = true,
  chrome = 'framed',
  extensions = [],
  toolbarControls,
  showToolbar = true,
  onEditorReady,
  ariaLabel
}: HtmlDocumentEditorProps) => {
  const editorRootRef = React.useRef<HTMLDivElement | null>(null);
  const [resizeControlsState, setResizeControlsState] =
    React.useState<EditorResizeControlsState>(null);

  const lastCanceledKeyRef = React.useRef<string | undefined>(
    artifactId !== undefined ? `${artifactId}:${artifactVersion ?? ''}` : undefined
  );

  // The editor is built once, so its handlers read the latest callbacks through refs.
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const persistence = useEditorDocumentPersistence({
    content,
    onUpdate,
    onSaveStatusChange
  });

  const { onCreate } = useEditorImageSetup({
    workspaceId,
    templateId,
    artifactId,
    setResizeControlsState
  });

  // TipTap only edits body HTML; avoid seeding full documents (content sync applies the prop).
  const initialEditorContent = React.useMemo(() => extractBodyContent(content), []);

  const editor = useEditor(
    {
      editable,
      extensions: [...buildEditorExtensions(EDITOR_SCOPE_ID), ...extensions],
      content: initialEditorContent,
      editorProps: { attributes: ariaLabel ? { 'aria-label': ariaLabel } : {} },
      onTransaction: ({ editor: ed, transaction }) => {
        if (
          !ed.isEditable ||
          !transaction.docChanged ||
          ed.getHTML() === persistence.lastContentRef.current ||
          persistence.isInitialMountRef.current
        ) {
          return;
        }

        const newContent = ed.getHTML();
        const candidateToSave = persistence.getContentToSave(newContent);
        onChangeRef.current?.(candidateToSave);
        if (candidateToSave === persistence.lastSavedContentRef.current) {
          persistence.lastContentRef.current = newContent;
          return;
        }

        persistence.lastContentRef.current = newContent;
        persistence.debouncedOnUpdate(newContent);
      },
      onBlur: async ({ editor: ed }) => {
        const currentHTML = ed.getHTML();
        if (!ed.isEditable || currentHTML === persistence.lastContentRef.current) {
          return;
        }
        persistence.lastContentRef.current = currentHTML;
        await persistence.flushSave(currentHTML);
      },
      onCreate
    },
    []
  );

  useEditorContentSync({
    editor,
    content,
    artifactId,
    getContentToSave: persistence.getContentToSave,
    documentPrefixRef: persistence.documentPrefixRef,
    documentSuffixRef: persistence.documentSuffixRef,
    lastContentRef: persistence.lastContentRef,
    lastSavedContentRef: persistence.lastSavedContentRef,
    isInitialMountRef: persistence.isInitialMountRef,
    awaitingSaveEchoRef: persistence.awaitingSaveEchoRef
  });

  const { cancelPendingSave } = persistence;

  React.useEffect(() => {
    const key = artifactId !== undefined ? `${artifactId}:${artifactVersion ?? ''}` : undefined;
    if (key === lastCanceledKeyRef.current) return;
    lastCanceledKeyRef.current = key;
    cancelPendingSave();
  }, [artifactId, artifactVersion, cancelPendingSave]);

  useEditorStylesheets({
    editorRootRef,
    content,
    scopeId: EDITOR_SCOPE_ID,
    workspaceId,
    artifactId,
    artifactVersion,
    templateId,
    showPreview
  });

  const preview = useEditorPreview({
    showPreview,
    workspaceId,
    artifactId,
    artifactVersion,
    templateId
  });

  React.useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  React.useEffect(() => {
    if (editor) onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  const showPreviewPane = Boolean(showPreview && preview.previewUrl);
  const bare = chrome === 'bare';

  const menuBar = (
    <MenuBar
      editor={editor as EditorType}
      showPreview={showPreview}
      onPreviewToggle={onPreviewToggle}
      showImageButton={showImageButton}
      subtle={bare}
    />
  );

  return (
    <div
      className={clsx(
        'relative flex flex-col flex-grow',
        // Bare grows with its content and lets the page scroll. `overflow-x`
        // is left alone rather than hidden: hiding one axis makes the other
        // `auto`, which would hand the writing area a scrollbar again and clip
        // the toolbar that is meant to stick to the page.
        !bare &&
          'mt-2 min-h-0 overflow-y-auto overflow-x-hidden rounded-lg border border-neutral-200 dark:border-neutral-700'
      )}
    >
      {showToolbar && (
        <div
          className={clsx(
            'sticky top-0 left-0 z-10 w-full bg-white dark:bg-neutral-800',
            bare ? 'border-b border-neutral-200 dark:border-neutral-700' : 'shadow-sm'
          )}
        >
          {menuBar}
          {editor && toolbarControls?.(editor)}
        </div>
      )}

      {showPreviewPane && preview.previewUrl ? (
        <EditorPreviewPane
          previewUrl={preview.previewUrl}
          previewKey={preview.previewKey}
          isIframeLoading={preview.isIframeLoading}
          onIframeLoad={preview.handleIframeLoad}
        />
      ) : (
        <div
          ref={editorRootRef}
          className={clsx('tiptap', bare ? 'flex-grow py-3' : 'p-4')}
          data-editor-scope={EDITOR_SCOPE_ID}
        >
          <EditorContent editor={editor} />
        </div>
      )}

      {resizeControlsState?.visible && editor && (
        <div
          style={{
            position: 'fixed',
            left: `${resizeControlsState.x}px`,
            top: `${resizeControlsState.y}px`
          }}
        >
          <ImageResizeControls
            width={resizeControlsState.width}
            height={resizeControlsState.height}
            onResize={(width, height) => {
              editor.commands.setImageSize({ width, height });
              setResizeControlsState(null);
            }}
            onDelete={() => {
              editor.commands.deleteImage();
              setResizeControlsState(null);
            }}
            onClose={() => setResizeControlsState(null)}
          />
        </div>
      )}
    </div>
  );
};

/** @deprecated Use `HtmlDocumentEditor` — kept for existing imports. */
export const Editor = HtmlDocumentEditor;
